import type { ReactNode } from "react";

import { autonomia } from "../lib/autonomia";
import { dataHora, duracao, num } from "../lib/format";
import type { StatusPoint } from "../types";

/** Sessões que cabem na tabela sem a transformar num histórico de reboots. */
const SESSOES_VISIVEIS = 6;

interface Props {
  /** Histórico de `status` da boia do painel, no intervalo escolhido. */
  status: StatusPoint[];
  /** O histórico bateu no teto de páginas — os números cobrem só o princípio. */
  truncado: boolean;
}

/**
 * Quanto tempo a boia leva ligada e quanto lhe resta — a régua do teste de
 * resistência: deixar o nó a correr até a bateria acabar e ver a história
 * completa num sítio só, em vez de a reconstruir a partir dos gráficos.
 *
 * Só da boia do painel, como a ondulação: são grandezas operacionais de cada
 * nó, e o histórico de `status` só se pede para a primeira.
 */
export function Autonomia({ status, truncado }: Props): ReactNode {
  const a = autonomia(status);

  return (
    <div className="seccao">
      <h3>Autonomia</h3>
      {status.length === 0 ? (
        // Ausência escrita por extenso, como em todo o lado: uma secção que
        // desaparecesse em silêncio lia-se como "não existe esta medição".
        <p className="nota" style={{ marginTop: 8, marginBottom: 0 }}>
          Sem blocos de estado no intervalo.
        </p>
      ) : (
        <>
          <div className="metricas">
            <div className="metrica">
              <span className="rotulo">Ligada há</span>
              <div className="valor" style={{ fontSize: 15 }}>
                {a.ligadoHa === null ? "—" : duracao(a.ligadoHa)}
              </div>
            </div>
            <div className="metrica">
              <span className="rotulo">Sessão anterior</span>
              <div className="valor" style={{ fontSize: 15 }}>
                {a.sessoes.length < 2 ? "—" : duracao(a.sessoes[a.sessoes.length - 2]!.duracao)}
              </div>
            </div>
            <div className="metrica">
              <span className="rotulo">Reinícios no intervalo</span>
              <div className="valor" style={{ fontSize: 15 }}>
                {a.reinicios}
              </div>
            </div>
            <div className="metrica">
              <span className="rotulo">Consumo médio</span>
              <div className="valor" style={{ fontSize: 15 }}>
                {a.consumoMedioMw === null ? "—" : `${num(a.consumoMedioMw / 1000, 2)} W`}
              </div>
            </div>
            <div className="metrica">
              <span className="rotulo">Energia no intervalo</span>
              <div className="valor" style={{ fontSize: 15 }}>
                {a.energiaWh === null ? "—" : `${num(a.energiaWh, 1)} Wh`}
              </div>
            </div>
            <div className="metrica">
              <span className="rotulo">Descarga</span>
              <div className="valor" style={{ fontSize: 15 }}>
                {a.descargaPctPorHora === null ? "—" : `${num(a.descargaPctPorHora, 2)} %/h`}
              </div>
            </div>
            <div className="metrica">
              <span className="rotulo">Restante estimado</span>
              <div className="valor" style={{ fontSize: 15 }}>
                {a.horasRestantes === null ? "—" : `≈ ${duracao(a.horasRestantes * 3600)}`}
              </div>
            </div>
          </div>
          {/* Newest primeiro: quem abre isto quer saber da última sessão, não
              da arqueologia. A duração conta desde o arranque real, mesmo que
              anterior à janela. */}
          <table className="disponibilidade" style={{ marginTop: 10 }}>
            <thead>
              <tr>
                <th scope="col">Sessão</th>
                <th scope="col">Arranque</th>
                <th scope="col">Último sinal</th>
                <th scope="col">Duração</th>
              </tr>
            </thead>
            <tbody>
              {a.sessoes
                .slice(-SESSOES_VISIVEIS)
                .reverse()
                .map((s, i) => (
                  <tr key={s.inicio}>
                    <th scope="row">{i === 0 ? "atual/última" : `−${i}`}</th>
                    <td>{dataHora(new Date(s.inicio).toISOString())}</td>
                    <td>{dataHora(new Date(s.ultimoSinal).toISOString())}</td>
                    <td className="valor">{duracao(s.duracao)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
          {a.sessoes.length > SESSOES_VISIVEIS && (
            <p className="nota" style={{ marginTop: 4, marginBottom: 0 }}>
              E mais {a.sessoes.length - SESSOES_VISIVEIS} sessões antigas no intervalo.
            </p>
          )}
          <p className="nota" style={{ marginTop: 8, marginBottom: 0 }}>
            Sobre o intervalo escolhido. "Ligada há" recomeça em cada arranque; a estimativa é a
            reta da descarga no intervalo e só aparece com a boia francamente a descarregar.
            {/* O histórico chega por ordem crescente: truncado = só o princípio
                da janela chegou, e estes números param aí. */}
            {truncado &&
              " Intervalo grande: os números cobrem só o princípio da janela — encurta o intervalo."}
          </p>
        </>
      )}
    </div>
  );
}
